import api from '../api';
import { newUuid } from '../collectionRecord';

/*
 * A Z-stack as the server keeps it (routes/media_bundles.py, migration 0095;
 * docs/FIELD_AI_VISION_SPEC.md 10.3): the layers go up first as the host's
 * ordinary media (Field Mode's batch photos, the dish station's plate photos,
 * the microscope's micrographs), then POST /media-bundles ties them together
 * with the camera's manifest.
 *
 *   manifestOf(stack, ...)   the manifest of a kept stack (useZStack's keep()):
 *                            schema 1, kind z_stack_bundle, its frames in
 *                            position order. The server writes each frame's
 *                            member itself.
 *   bundleBody(stack, ids)   the POST body for the layers that reached the
 *                            server: a layer that did not is left out and the
 *                            positions close up; null when fewer than two did.
 *   uploadStack(stack, ...)  the dish station's and the microscope's way: each
 *                            layer up, then the stack registered.
 *   newBatchBundle(),        a stack as a Field Mode batch holds it for the
 *   dropFromBundles()        outbox's Bundles step, and a photo removed from it.
 *   collapseBundles(list)    a photo or micrograph list with each stack shown
 *                            by its cover alone (the lists carry bundle_id,
 *                            bundle_cover and bundle_layers).
 */
const finite = v => (Number.isFinite(v) ? v : null);
const rounded = v => (Number.isFinite(v) ? Math.round(v * 1000) / 1000 : null);

export function manifestOf(stack, { viewport, device = {} } = {}) {
  return {
    schema: 1,
    kind: 'z_stack_bundle',
    path: stack.path,
    order: stack.order,
    viewport,
    device: { label: String(device.label || '').slice(0, 200), facing: device.facing || 'environment' },
    frames: stack.layers.map((layer, position) => ({
      position, focus_distance: finite(layer.focus_distance), t_ms: Math.round(layer.t_ms || 0),
      sharpness: rounded(layer.sharpness), tile_wins: layer.tile_wins || 0, kept: layer.kept || 'sharp',
    })),
    rejected: { blurred: 0, weak: 0, duplicate: 0, over_cap: 0, ...(stack.rejected || {}) },
    settings: { ...(stack.settings || {}) },
  };
}

/**
 * POST /media-bundles's body: `ids` has one entry per layer of `stack`, in
 * position order -- the server's photo_id or microscopy_image_id (`medium`),
 * or null for a layer that did not go up.
 */
export function bundleBody(stack, ids, { medium, viewport, device } = {}) {
  const manifest = manifestOf(stack, { viewport, device });
  const kept = stack.layers.map((layer, i) => ({ layer, i, id: ids[i] || null })).filter(x => x.id);
  if (kept.length < 2) return null;
  let cover = kept.findIndex(x => x.i === stack.cover);
  if (cover < 0) {
    cover = 0;
    kept.forEach((x, i) => { if ((x.layer.sharpness || 0) > (kept[cover].layer.sharpness || 0)) cover = i; });
  }
  return {
    kind: 'z_stack',
    cover_position: cover,
    members: kept.map((x, position) => ({
      position, [medium]: x.id, focus_distance: finite(x.layer.focus_distance),
      sharpness: rounded(x.layer.sharpness), t_ms: Math.round(x.layer.t_ms || 0),
    })),
    manifest: { ...manifest, frames: kept.map((x, position) => ({ ...manifest.frames[x.i], position })) },
  };
}

export async function registerBundle(body, config) {
  const { data } = await api.post('/media-bundles', body, ...(config ? [config] : []));
  return data;
}

/**
 * A kept stack sent the way the dish station and the microscope send one:
 * each layer by `uploadLayer(layer, i)` -> the server's id or null (one at a
 * time: a phone's connection takes one well), then the stack registered.
 * Never rejects. {ids, uploaded, failed, bundle, bundleError, coverId}: the
 * cover's id, or the first layer's that went up, is what the page opens.
 */
export async function uploadStack(stack, { uploadLayer, medium, viewport, onProgress, register = registerBundle }) {
  const ids = [];
  for (let i = 0; i < stack.layers.length; i += 1) {
    let id = null;
    try {
      id = await uploadLayer(stack.layers[i], i);
    } catch {
      id = null;
    }
    ids.push(id || null);
    onProgress?.(i + 1, stack.layers.length);
  }
  const uploaded = ids.filter(Boolean).length;
  const body = bundleBody(stack, ids, { medium, viewport, device: stack.device });
  let bundle = null;
  let bundleError = null;
  if (body) {
    try {
      bundle = await register(body);
    } catch (e) {
      bundleError = e;
    }
  }
  return { ids, uploaded, failed: ids.length - uploaded, bundle, bundleError,
           coverId: ids[stack.cover] || ids.find(Boolean) || null };
}

export async function fetchBundle(bundleId) {
  const { data } = await api.get(`/media-bundles/${bundleId}`);
  return data;
}

/**
 * A kept stack as a Field Mode batch holds it until the outbox's Bundles
 * step sends it (utils/fieldOutbox.js): which batch photograph each layer is
 * (`photoIds`, by layer), what the manifest needs, never the JPEGs (they are
 * the batch's photos).
 */
export function newBatchBundle(stack, photoIds, { device } = {}) {
  return {
    id: newUuid(),
    kind: 'z_stack',
    sent: false,
    stack: {
      path: stack.path, order: stack.order, cover: stack.cover, rejected: { ...(stack.rejected || {}) },
      settings: { ...(stack.settings || {}) }, device: device || stack.device || null,
      layers: stack.layers.map((layer, i) => ({
        photoId: photoIds[i], focus_distance: finite(layer.focus_distance), t_ms: Math.round(layer.t_ms || 0),
        sharpness: rounded(layer.sharpness), tile_wins: layer.tile_wins || 0, kept: layer.kept || 'sharp',
      })),
    },
  };
}

/** A batch's stacks without the photograph `photoId`: its stack's cover moves to the sharpest left, and a stack
 *  left with fewer than two layers goes. Sent stacks are kept as they are. */
export function dropFromBundles(bundles, photoId) {
  return (bundles || []).flatMap((bundle) => {
    if (bundle.sent || !bundle.stack.layers.some(l => l.photoId === photoId)) return [bundle];
    const coverId = bundle.stack.layers[bundle.stack.cover]?.photoId;
    const layers = bundle.stack.layers.filter(l => l.photoId !== photoId);
    if (layers.length < 2) return [];
    let cover = layers.findIndex(l => l.photoId === coverId);
    if (cover < 0) {
      cover = 0;
      layers.forEach((l, i) => { if ((l.sharpness || 0) > (layers[cover].sharpness || 0)) cover = i; });
    }
    return [{ ...bundle, stack: { ...bundle.stack, layers, cover } }];
  });
}

/** Each stack shown by its cover alone; where the list has no cover of a stack, by its first layer listed. */
export function collapseBundles(items) {
  const covered = new Set((items || []).filter(i => i.bundle_id && i.bundle_cover).map(i => i.bundle_id));
  const shown = new Set();
  return (items || []).filter((item) => {
    if (!item.bundle_id) return true;
    if (shown.has(item.bundle_id)) return false;
    if (covered.has(item.bundle_id) && !item.bundle_cover) return false;
    shown.add(item.bundle_id);
    return true;
  });
}
